import { MessageRow } from '../../../types/supabase';
import { useSession } from '../../providers/supabase';

interface MessageSummaryProps {
  message: MessageRow;
  showDirection?: boolean;
  onClick?: () => void;
  className?: string;
}

export function MessageSummary({ 
  message, 
  showDirection = true, 
  onClick,
  className = ""
}: MessageSummaryProps) {
  const session = useSession();
  const getMessageIcon = (message: MessageRow) => {
    // For now, email is the only communication method
    return 'mail';
  };

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const getSenderType = () => {
    // In-app replies are from the user
    if (message.direction === 'INBOUND' && message.channel === 'in-app') {
      return 'user';
    }

    const currentUserEmail = session?.user?.email;
    const senderEmail = message.from_address;

    if (senderEmail === currentUserEmail) {
      return 'user';
    } else if (senderEmail?.includes('healthsharetech.com') || senderEmail?.includes('@localhost')) {
      return 'system';
    } else {
      return 'provider';
    }
  };

  const getSenderIcon = () => {
    if (message.direction === 'INBOUND') {
      return 'arrow_downward';
    } else if (message.direction === 'OUTBOUND') {
      return 'arrow_outward';
    } else {
      return 'question_mark';
    }
  };

  const getBadgeColor = () => {
    const senderType = getSenderType();
    switch (senderType) {
      case 'user':
        return 'bg-success text-white';
      case 'system':
        return 'bg-light text-dark';
      case 'provider':
        return 'bg-primary text-white';
      default:
        return 'bg-light text-dark';
    }
  };

  return (
    <div 
      className={`message-summary ${className}`}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : {}}
    >
      <div className="d-flex align-items-center mb-1">
        {showDirection && (
          <div className={`badge me-2 ${getBadgeColor()}`}>
            <i className="material-icons" style={{ fontSize: '14px', marginRight: '4px' }}>
              {getMessageIcon(message)}
            </i>
            <i className="material-icons" style={{ fontSize: '14px' }}>
              {getSenderIcon()}
            </i>
          </div>
        )}
        {message.status && (
          <div className={`badge me-2 ${message.status === 'delivered' ? 'bg-success' : 
                                       message.status === 'pending' ? 'bg-warning' : 
                                       message.status === 'failed' ? 'bg-danger' : 'bg-secondary'}`}>
            {message.status}
          </div>
        )}
        <div className="small text-muted">
          {formatMessageTime(message.created_at)}
        </div>
      </div>
      
      <div className="text-muted small">
        <div className="mb-1">
          {message.subject ? (
            <span><strong>Subject:</strong> {message.subject}</span>
          ) : (
            <span>No subject</span>
          )}
        </div>
        {message.body_text && (
          <div style={{ 
            maxHeight: '2.4em', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical' as const
          }}>
            {message.body_text}
          </div>
        )}
      </div>
    </div>
  );
}